// THE FIELD IS THE PROGRESS BAR — pure rules (no scene, no timers) for how the rice rows grow with the player's clicks.
//
// A season has a target of RiceGrowth.clicksPerPaddy clicks per rice paddy (shown under the clicker as "37 / 125 rice"). The rows
// of all paddies are filled in ROUND-ROBIN order — row 0 of every paddy, then row 1 of every paddy, … — so the whole kingdom fills
// evenly: with 5 rows a paddy, each row takes clicksPerPaddy / 5 clicks and grows a notch per click. A row's height runs from the
// season's start height (the previous season's full height; stubble after the harvest) to the season's full height
// (SeasonConfig.plantScale), and its tint from the season's start tint to its end tint (green → gold in HARVEST).
// This is the real count — nothing here invents progress. Tests: scripts/qa/ricegrowth.test.mjs.

import { SeasonOrder, SeasonConfig } from '../config/balance.js'

export const ROWS_PER_PADDY = 5
const clamp01 = (v) => Math.max(0, Math.min(1, v))
const lerp = (a, b, t) => a + (b - a) * t

// How many clicks fill the season, for this many paddies.
export const growthTarget = (paddies, cfg) => Math.max(0, paddies) * cfg.clicksPerPaddy
// Clicks that fill one row.
export const clicksPerRow = (cfg) => cfg.clicksPerPaddy / ROWS_PER_PADDY

// The place of (paddy p, row r) in the fill order: round-robin across paddies.
export const rowOrder = (paddyIdx, rowIdx, paddies) => rowIdx * paddies + paddyIdx

// 0…1: how full the row at order k is after `clicks` clicks this season.
export function rowProgress(k, clicks, cfg) {
  const per = clicksPerRow(cfg)
  return clamp01((clicks - k * per) / per)
}

// The row the NEXT click goes to (its order), or null when every row is full (or there are no paddies).
export function currentRowOrder(clicks, paddies, cfg) {
  const rows = paddies * ROWS_PER_PADDY
  if (!rows) return null
  const k = Math.floor(clicks / clicksPerRow(cfg))
  return k < rows ? k : null
}

// The season's start and full heights for a row (scale of the row piece).
export function seasonScales(season, cfg) {
  const i = SeasonOrder.indexOf(season)
  const end = SeasonConfig[season]?.plantScale ?? 1
  const start = i <= 0 ? cfg.stubbleScale : (SeasonConfig[SeasonOrder[i - 1]]?.plantScale ?? end)
  return { start, end }
}
export function rowScale(season, progress, cfg) {
  const { start, end } = seasonScales(season, cfg)
  return lerp(start, end, clamp01(progress))
}

// Tints multiply the row's material colour: mud-dull in PREP, pale shoots → lush in PLANT/MAINTAIN, green → gold in HARVEST.
const TINT = {
  PREP: [[0.86, 0.80, 0.68], [0.95, 0.92, 0.82]],
  PLANT: [[0.95, 0.92, 0.82], [0.96, 1.0, 0.9]],
  MAINTAIN: [[0.96, 1.0, 0.9], [1, 1, 1]],
  HARVEST: [[1, 1, 1], [1.18, 1.02, 0.58]],
}
export function rowTint(season, progress) {
  const [a, b] = TINT[season] ?? TINT.MAINTAIN
  const t = clamp01(progress)
  return [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)]
}
